import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { MessageSquare, Loader2, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ChatList() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, token } = useAuth();

  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchChats = async () => {
      try {
        const response = await fetch(`${API_URL}/api/chats`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          setChats(data);
        }
      } catch (error) {
        console.error('Failed to fetch chats:', error);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchChats();
    }
  }, [token]);

  const timeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000 / 60);
    
    if (diff < 60) return `${diff}m`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h`;
    return `${Math.floor(diff / 1440)}d`;
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-24 md:pb-0">
      <Navbar />

      <div className="max-w-2xl mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold text-slate-900 mb-6">{t('chat.title')}</h1>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
          </div>
        ) : chats.length === 0 ? (
          <div className="text-center py-20">
            <MessageSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">{t('chat.no_chats')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {chats.map((chat) => (
              <button
                key={chat.chat_id}
                data-testid={`chat-item-${chat.chat_id}`}
                onClick={() => navigate(`/chat/${chat.chat_id}`)}
                className="w-full flex items-center gap-4 p-4 bg-white rounded-xl border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all text-left"
              >
                <Avatar className="h-14 w-14 shrink-0">
                  <AvatarImage src={chat.other_user?.photo_url} />
                  <AvatarFallback className="bg-slate-200">
                    {chat.other_user?.name?.charAt(0)?.toUpperCase() || '?'}
                  </AvatarFallback>
                </Avatar>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-semibold text-slate-900 truncate">
                      {chat.other_user?.name || 'Unknown'}
                    </p>
                    {chat.last_message && (
                      <span className="text-xs text-slate-400 flex items-center gap-1 shrink-0">
                        <Clock className="w-3 h-3" />
                        {timeAgo(chat.last_message.created_at)}
                      </span>
                    )}
                  </div>

                  {chat.job && (
                    <Badge variant="secondary" className="mt-1 mb-2">
                      {chat.job.title}
                    </Badge>
                  )}

                  {chat.last_message && (
                    <p className="text-sm text-slate-500 truncate">
                      {chat.last_message.sender_id === user?.user_id ? 'You: ' : ''}
                      {chat.last_message.content}
                    </p>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
